<template>
  <!--
    Модалка «Личное дело» — 5 вкладок по Figma «Проектирование» (страница 0:1).
    По ТЗ 02.09.2026: 110 полей у каждого военнослужащего в КПС/ЧПГО.
    Данные грузятся/сохраняются через /api/users/admin/{id}/ (все 110 полей уже
    в AdminUserListSerializer + AdminUpdateUserSerializer).
  -->
  <div v-if="open" class="pdm__backdrop" @mousedown.self="onClose">
    <div class="pdm" role="dialog" aria-modal="true" @mousedown.stop>
      <header class="pdm__head">
        <h3 class="pdm__title">{{ $t('personnel.detailModal.titleWithName', { name: form.full_name || $t('personnel.detailModal.serviceman') }) }}</h3>
        <button class="pdm__close" @click="onClose" :aria-label="$t('personnel.detailModal.close')">×</button>
      </header>

      <nav class="pdm__tabs">
        <button
          v-for="t in tabs"
          :key="t.key"
          class="pdm__tab"
          :class="{ 'is-active': tab === t.key }"
          @click="tab = t.key"
        >{{ t.label }}</button>
      </nav>

      <div class="pdm__body">
        <div v-if="loading" class="pdm__state">{{ $t('personnel.detailModal.loading') }}</div>
        <div v-else-if="error" class="pdm__state pdm__state--err">{{ error }}</div>

        <!-- ============================================================ ОБЩИЕ -->
        <div v-else-if="tab === 'common'" class="pdm__grid">
          <label class="pdm__field"><span>{{ $t('detail.commonName') }}</span><input v-model="form.full_name" /></label>
          <label class="pdm__field"><span>{{ $t('detail.personalNumber') }}</span><input v-model.number="form.personal_number" type="number" /></label>
          <label class="pdm__field"><span>{{ $t('detail.iin') }}</span><input v-model="form.iin" readonly /></label>
          <label class="pdm__field"><span>{{ $t('detail.birthDate') }}</span><input v-model="form.birth_date" type="date" /></label>
          <label class="pdm__field pdm__field--wide"><span>{{ $t('detail.birthplace') }}</span><input v-model="form.birthplace" /></label>
          <label class="pdm__field pdm__field--wide"><span>{{ $t('detail.address') }}</span><input v-model="form.address" /></label>
          <label class="pdm__field"><span>{{ $t('detail.phone') }}</span><input v-model="form.phone_number" /></label>
          <label class="pdm__field"><span>{{ $t('detail.idNumber') }}</span><input v-model.number="form.id_number" type="number" /></label>
          <label class="pdm__field"><span>{{ $t('detail.familyStatus') }}</span><input v-model="form.family_status" /></label>
          <label class="pdm__field"><span>{{ $t('detail.serviceStart') }}</span><input v-model="form.service_start_date" type="date" /></label>
          <label class="pdm__field pdm__field--wide"><span>{{ $t('detail.recruitmentOffice') }}</span><input v-model="form.recruitment_office" /></label>
          <label class="pdm__field"><span>{{ $t('detail.appointmentDate') }}</span><input v-model="form.appointment_date" type="date" /></label>
          <label class="pdm__field"><span>{{ $t('detail.appointmentOrder') }}</span><input v-model.number="form.appointment_order_number" type="number" /></label>
          <label class="pdm__field"><span>{{ $t('detail.sentryLeaderDate') }}</span><input v-model="form.sentry_leader_date" type="date" /></label>
          <label class="pdm__field"><span>{{ $t('detail.sentryLeaderOrder') }}</span><input v-model.number="form.sentry_leader_order_number" type="number" /></label>
          <label class="pdm__field"><span>{{ $t('detail.classQualification') }}</span><input v-model="form.class_qualification" /></label>
          <label class="pdm__field"><span>{{ $t('detail.classDate') }}</span><input v-model="form.class_qualification_date" type="date" /></label>
          <label class="pdm__field"><span>{{ $t('detail.classOrder') }}</span><input v-model.number="form.class_qualification_order_number" type="number" /></label>
          <label class="pdm__field"><span>{{ $t('detail.sportRank') }}</span><input v-model.number="form.sport_rank" type="number" /></label>
          <label class="pdm__field"><span>{{ $t('detail.sportYear') }}</span><input v-model.number="form.sport_rank_year" type="number" /></label>
          <label class="pdm__field"><span>{{ $t('detail.sportType') }}</span><input v-model="form.sport_type" /></label>
          <label class="pdm__field"><span>{{ $t('detail.combatTraining1') }}</span><input v-model.number="form.combat_training_first_period" type="number" /></label>
          <label class="pdm__field"><span>{{ $t('detail.combatTraining2') }}</span><input v-model.number="form.combat_training_second_period" type="number" /></label>
          <label class="pdm__field"><span>{{ $t('detail.arrivalDate') }}</span><input v-model="form.arrival_date" type="date" /></label>
          <label class="pdm__field pdm__field--wide"><span>{{ $t('detail.arrivalUnit') }}</span><input v-model="form.arrival_unit" /></label>
          <label class="pdm__field"><span>{{ $t('detail.departureDate') }}</span><input v-model="form.departure_date" type="date" /></label>
          <label class="pdm__field pdm__field--wide"><span>{{ $t('detail.departureUnit') }}</span><input v-model="form.departure_unit" /></label>
          <label class="pdm__field"><span>{{ $t('detail.educationCategory') }}</span><input v-model="form.education_category" /></label>
          <label class="pdm__field"><span>{{ $t('detail.educationYear') }}</span><input v-model.number="form.education_year" type="number" /></label>
          <label class="pdm__field pdm__field--wide"><span>{{ $t('detail.educationInstitution') }}</span><input v-model="form.education_institution" /></label>
          <label class="pdm__field pdm__field--wide"><span>{{ $t('detail.educationSpecialty') }}</span><input v-model="form.education_specialty" /></label>
        </div>

        <!-- ============================================================ ВСПР/ВИР -->
        <div v-else-if="tab === 'vspr'" class="pdm__grid">
          <label class="pdm__field pdm__field--full"><span>{{ $t('detail.familyComposition') }}</span><textarea v-model="form.family_composition" rows="3"></textarea></label>
          <label class="pdm__field pdm__field--full"><span>{{ $t('detail.addressBefore') }}</span><textarea v-model="form.address_before_service" rows="2"></textarea></label>
          <label class="pdm__field pdm__field--full"><span>{{ $t('detail.gndStatus') }}</span><input v-model="form.gnd_status" /></label>
          <label class="pdm__field pdm__field--full"><span>{{ $t('detail.adminSanctions') }}</span><textarea v-model="form.admin_sanctions" rows="2"></textarea></label>
          <label class="pdm__field pdm__field--full"><span>{{ $t('detail.criminalLiability') }}</span><textarea v-model="form.criminal_liability" rows="2"></textarea></label>
          <label class="pdm__field pdm__field--full"><span>{{ $t('detail.specialMarks') }}</span><textarea v-model="form.special_marks" rows="2"></textarea></label>

          <div class="pdm__jsontable pdm__field--full">
            <div class="pdm__jsonhead">
              <strong>{{ $t('personnel.detailModal.sections.awards') }}</strong>
              <button type="button" class="pdm__addbtn" @click="addRow('awards', { type: '', date: '', giver: '' })">{{ $t('personnel.detailModal.addRow') }}</button>
            </div>
            <div v-for="(row, i) in form.awards" :key="'aw' + i" class="pdm__jsonrow">
              <input :placeholder="$t('personnel.detailModal.placeholders.award')" v-model="row.type" />
              <input :placeholder="$t('personnel.detailModal.placeholders.date')" type="date" v-model="row.date" />
              <input :placeholder="$t('personnel.detailModal.placeholders.giver')" v-model="row.giver" />
              <button type="button" class="pdm__delbtn" @click="delRow('awards', i)">×</button>
            </div>
          </div>

          <div class="pdm__jsontable pdm__field--full">
            <div class="pdm__jsonhead">
              <strong>{{ $t('personnel.detailModal.sections.rewards') }}</strong>
              <button type="button" class="pdm__addbtn" @click="addRow('rewards', { type: '', date: '', reason: '', giver: '' })">{{ $t('personnel.detailModal.addRow') }}</button>
            </div>
            <div v-for="(row, i) in form.rewards" :key="'rw' + i" class="pdm__jsonrow">
              <input :placeholder="$t('personnel.detailModal.placeholders.type')" v-model="row.type" />
              <input :placeholder="$t('personnel.detailModal.placeholders.date')" type="date" v-model="row.date" />
              <input :placeholder="$t('personnel.detailModal.placeholders.reason')" v-model="row.reason" />
              <input :placeholder="$t('personnel.detailModal.placeholders.giverBy')" v-model="row.giver" />
              <button type="button" class="pdm__delbtn" @click="delRow('rewards', i)">×</button>
            </div>
          </div>

          <div class="pdm__jsontable pdm__field--full">
            <div class="pdm__jsonhead">
              <strong>{{ $t('personnel.detailModal.sections.disciplinary') }}</strong>
              <button type="button" class="pdm__addbtn" @click="addRow('disciplinary_sanctions', { type: '', date: '', reason: '', giver: '', removed_date: '' })">{{ $t('personnel.detailModal.addRow') }}</button>
            </div>
            <div v-for="(row, i) in form.disciplinary_sanctions" :key="'ds' + i" class="pdm__jsonrow">
              <input :placeholder="$t('personnel.detailModal.placeholders.sanction')" v-model="row.type" />
              <input :placeholder="$t('personnel.detailModal.placeholders.date')" type="date" v-model="row.date" />
              <input :placeholder="$t('personnel.detailModal.placeholders.reason')" v-model="row.reason" />
              <input :placeholder="$t('personnel.detailModal.placeholders.giverBy')" v-model="row.giver" />
              <input :placeholder="$t('personnel.detailModal.placeholders.removedDate')" type="date" v-model="row.removed_date" />
              <button type="button" class="pdm__delbtn" @click="delRow('disciplinary_sanctions', i)">×</button>
            </div>
          </div>

          <div class="pdm__jsontable pdm__field--full">
            <div class="pdm__jsonhead">
              <strong>{{ $t('personnel.detailModal.sections.relatives') }}</strong>
              <button type="button" class="pdm__addbtn" @click="addRow('relatives_info', { relation: '', fio: '', address: '', occupation: '', phone: '' })">{{ $t('personnel.detailModal.addRow') }}</button>
            </div>
            <div v-for="(row, i) in form.relatives_info" :key="'rel' + i" class="pdm__jsonrow">
              <input :placeholder="$t('personnel.detailModal.placeholders.relation')" v-model="row.relation" />
              <input :placeholder="$t('personnel.detailModal.placeholders.fio')" v-model="row.fio" />
              <input :placeholder="$t('personnel.detailModal.placeholders.address')" v-model="row.address" />
              <input :placeholder="$t('personnel.detailModal.placeholders.occupation')" v-model="row.occupation" />
              <input :placeholder="$t('personnel.detailModal.placeholders.phone')" v-model="row.phone" />
              <button type="button" class="pdm__delbtn" @click="delRow('relatives_info', i)">×</button>
            </div>
          </div>
        </div>

        <!-- ============================================================ КАДРЫ -->
        <div v-else-if="tab === 'hr'" class="pdm__grid">
          <label class="pdm__field"><span>{{ $t('detail.contractDate') }}</span><input v-model="form.contract_date" type="date" /></label>
          <label class="pdm__field"><span>{{ $t('detail.contractDuration') }}</span><input v-model.number="form.contract_duration" type="number" /></label>
          <label class="pdm__field"><span>{{ $t('detail.contractType') }}</span><input v-model="form.contract_type" /></label>
          <label class="pdm__field"><span>{{ $t('detail.contractEnd') }}</span><input v-model="form.contract_end_date" type="date" /></label>
          <label class="pdm__field"><span>{{ $t('detail.serviceYearsCalendar') }}</span><input v-model.number="form.service_years_calendar" type="number" /></label>
          <label class="pdm__field"><span>{{ $t('detail.serviceYearsPrivileged') }}</span><input v-model.number="form.service_years_privileged" type="number" /></label>
          <label class="pdm__field"><span>{{ $t('detail.serviceYearsTotal') }}</span><input v-model.number="form.service_years_total" type="number" /></label>
          <label class="pdm__field"><span>{{ $t('detail.lastAttestation') }}</span><input v-model="form.last_attestation_date" type="date" /></label>
          <label class="pdm__field pdm__field--full"><span>{{ $t('detail.attestationConclusions') }}</span><textarea v-model="form.attestation_conclusions" rows="2"></textarea></label>
          <label class="pdm__field pdm__field--full"><span>{{ $t('detail.attestationImplementation') }}</span><textarea v-model="form.attestation_implementation" rows="2"></textarea></label>
          <label class="pdm__field pdm__field--full"><span>{{ $t('personnel.detailModal.familySpecialStatusLong') }}</span><textarea v-model="form.family_special_status" rows="2"></textarea></label>
          <label class="pdm__field"><span>{{ $t('detail.specialCheckDate') }}</span><input v-model="form.special_check_date" type="date" /></label>
          <label class="pdm__field"><span>{{ $t('detail.specialCheckForm') }}</span><input v-model="form.special_check_access_form" /></label>
          <label class="pdm__field pdm__field--wide"><span>{{ $t('detail.foreignLanguages') }}</span><input v-model="form.foreign_languages" /></label>
          <label class="pdm__field"><span>{{ $t('detail.rankAssignmentDate') }}</span><input v-model="form.rank_assignment_date" type="date" /></label>
          <label class="pdm__field"><span>{{ $t('detail.nextRankDate') }}</span><input v-model="form.next_rank_date" type="date" /></label>
          <label class="pdm__field pdm__field--full"><span>{{ $t('personnel.detailModal.socialBenefitsLong') }}</span><textarea v-model="form.social_benefits" rows="2"></textarea></label>
          <label class="pdm__field"><span>{{ $t('detail.serviceIdNumber') }}</span><input v-model="form.service_id_number" /></label>
          <label class="pdm__field"><span>{{ $t('detail.serviceIdIssue') }}</span><input v-model="form.service_id_issue_date" type="date" /></label>
          <label class="pdm__field"><span>{{ $t('detail.serviceIdExpiry') }}</span><input v-model="form.service_id_expiry_date" type="date" /></label>
          <label class="pdm__field"><span>{{ $t('detail.militaryIdNumber') }}</span><input v-model="form.military_id_number" /></label>
          <label class="pdm__field"><span>{{ $t('detail.militaryIdIssue') }}</span><input v-model="form.military_id_issue_date" type="date" /></label>
          <label class="pdm__field pdm__field--wide"><span>{{ $t('detail.militaryIdIssuePlace') }}</span><input v-model="form.military_id_issue_place" /></label>
        </div>

        <!-- ============================================================ МЕДИКИ -->
        <div v-else-if="tab === 'med'" class="pdm__grid">
          <label class="pdm__field"><span>{{ $t('detail.medAgeGroup') }}</span><input v-model="form.medical_age_group" /></label>
          <label class="pdm__field"><span>{{ $t('detail.healthGroup') }}</span><input v-model="form.health_group" /></label>
          <label class="pdm__field"><span>{{ $t('detail.bloodGroup') }}</span><input v-model="form.blood_group" /></label>
          <label class="pdm__field"><span>{{ $t('detail.weight') }}</span><input v-model.number="form.weight_kg" type="number" step="0.1" /></label>
          <label class="pdm__field"><span>{{ $t('detail.height') }}</span><input v-model.number="form.height_cm" type="number" /></label>
          <label class="pdm__field"><span>{{ $t('detail.bmi') }}</span><input v-model.number="form.bmi" type="number" step="0.1" /></label>
          <label class="pdm__field"><span>{{ $t('detail.lastDispensary') }}</span><input v-model="form.last_dispensary_date" type="date" /></label>
          <label class="pdm__field pdm__field--full"><span>{{ $t('detail.dispensaryConclusions') }}</span><textarea v-model="form.dispensary_conclusions" rows="2"></textarea></label>
          <label class="pdm__field pdm__field--full"><span>{{ $t('detail.dispensaryRecommendations') }}</span><textarea v-model="form.dispensary_recommendations" rows="2"></textarea></label>
          <label class="pdm__field pdm__field--full"><span>{{ $t('detail.dispensaryObservation') }}</span><textarea v-model="form.dispensary_observation" rows="2"></textarea></label>
          <label class="pdm__field"><span>{{ $t('detail.exemptionDays') }}</span><input v-model.number="form.exemption_days_count" type="number" /></label>
          <label class="pdm__field pdm__field--wide"><span>{{ $t('detail.medicalOrganization') }}</span><input v-model="form.medical_organization" /></label>

          <div class="pdm__jsontable pdm__field--full">
            <div class="pdm__jsonhead">
              <strong>{{ $t('personnel.detailModal.sections.outpatient') }}</strong>
              <button type="button" class="pdm__addbtn" @click="addRow('outpatient_treatment', { start_date: '', end_date: '', days: null, diagnosis: '' })">{{ $t('personnel.detailModal.addRow') }}</button>
            </div>
            <div v-for="(row, i) in form.outpatient_treatment" :key="'op' + i" class="pdm__jsonrow">
              <input :placeholder="$t('personnel.detailModal.placeholders.from')" type="date" v-model="row.start_date" />
              <input :placeholder="$t('personnel.detailModal.placeholders.to')" type="date" v-model="row.end_date" />
              <input :placeholder="$t('personnel.detailModal.placeholders.days')" type="number" v-model.number="row.days" />
              <input :placeholder="$t('personnel.detailModal.placeholders.diagnosis')" v-model="row.diagnosis" />
              <button type="button" class="pdm__delbtn" @click="delRow('outpatient_treatment', i)">×</button>
            </div>
          </div>

          <div class="pdm__jsontable pdm__field--full">
            <div class="pdm__jsonhead">
              <strong>{{ $t('personnel.detailModal.sections.inpatient') }}</strong>
              <button type="button" class="pdm__addbtn" @click="addRow('inpatient_treatment', { start_date: '', end_date: '', days: null, diagnosis: '' })">{{ $t('personnel.detailModal.addRow') }}</button>
            </div>
            <div v-for="(row, i) in form.inpatient_treatment" :key="'ip' + i" class="pdm__jsonrow">
              <input :placeholder="$t('personnel.detailModal.placeholders.from')" type="date" v-model="row.start_date" />
              <input :placeholder="$t('personnel.detailModal.placeholders.to')" type="date" v-model="row.end_date" />
              <input :placeholder="$t('personnel.detailModal.placeholders.days')" type="number" v-model.number="row.days" />
              <input :placeholder="$t('personnel.detailModal.placeholders.diagnosis')" v-model="row.diagnosis" />
              <button type="button" class="pdm__delbtn" @click="delRow('inpatient_treatment', i)">×</button>
            </div>
          </div>
        </div>

        <!-- ============================================================ ТЫЛ -->
        <div v-else-if="tab === 'sup'" class="pdm__grid">
          <label class="pdm__field"><span>{{ $t('detail.chestSize') }}</span><input v-model.number="form.chest_size" type="number" /></label>
          <label class="pdm__field"><span>{{ $t('detail.waistSize') }}</span><input v-model.number="form.waist_size" type="number" /></label>
          <label class="pdm__field"><span>{{ $t('detail.uniformSize') }}</span><input v-model="form.uniform_size" /></label>
          <label class="pdm__field"><span>{{ $t('detail.shoeSize') }}</span><input v-model="form.shoe_size" /></label>
          <label class="pdm__field"><span>{{ $t('detail.headwearSize') }}</span><input v-model="form.headwear_size" /></label>
          <label class="pdm__field pdm__field--full pdm__field--check"><input type="checkbox" v-model="form.needs_housing" /><span>{{ $t('detail.needsHousing') }}</span></label>
          <label class="pdm__field pdm__field--full pdm__field--check"><input type="checkbox" v-model="form.has_service_housing" /><span>{{ $t('detail.hasServiceHousing') }}</span></label>
          <label class="pdm__field pdm__field--full pdm__field--check"><input type="checkbox" v-model="form.has_own_housing" /><span>{{ $t('detail.hasOwnHousing') }}</span></label>
          <label class="pdm__field pdm__field--full pdm__field--check"><input type="checkbox" v-model="form.rents_housing" /><span>{{ $t('detail.rentsHousing') }}</span></label>
          <label class="pdm__field pdm__field--full pdm__field--check"><input type="checkbox" v-model="form.housing_payments_recipient" /><span>{{ $t('detail.housingPaymentsRecipient') }}</span></label>
          <label class="pdm__field pdm__field--full pdm__field--check"><input type="checkbox" v-model="form.privatization_compensation" /><span>{{ $t('detail.privatizationCompensation') }}</span></label>
        </div>
      </div>

      <footer class="pdm__foot">
        <div class="pdm__hint" v-if="saveError">{{ saveError }}</div>
        <button class="pdm__btn pdm__btn--ghost" @click="onClose">{{ $t('personnel.detailModal.cancel') }}</button>
        <button class="pdm__btn pdm__btn--primary" :disabled="saving" @click="onSave">
          {{ saving ? $t('personnel.detailModal.saving') : $t('personnel.detailModal.save') }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import { getAdminUser, updateAdminUser } from '@/api/adminUsers'

// По ТЗ 02.09.2026: JSON-массивы (награды, лечение и т.п.) хранятся списками
// объектов; UI даёт add/del по одной строке чтобы не изобретать формы.

const EMPTY_JSON_FIELDS = [
  'awards', 'rewards', 'disciplinary_sanctions', 'relatives_info',
  'outpatient_treatment', 'inpatient_treatment',
]

export default {
  name: 'PersonnelDetailModal',
  props: {
    open: { type: Boolean, default: false },
    personId: { type: [Number, String], default: null },
  },
  emits: ['close', 'saved'],
  data() {
    return {
      tab: 'common',
      loading: false,
      saving: false,
      error: null,
      saveError: null,
      form: this.blankForm(),
    }
  },
  computed: {
    tabs() {
      return [
        { key: 'common', label: this.$t('personnel.detailModal.tabCommon') },
        { key: 'vspr',   label: this.$t('personnel.detailModal.tabVspr') },
        { key: 'hr',     label: this.$t('personnel.detailModal.tabHr') },
        { key: 'med',    label: this.$t('personnel.detailModal.tabMed') },
        { key: 'sup',    label: this.$t('personnel.detailModal.tabSup') },
      ]
    },
  },
  watch: {
    open(v) {
      if (v) this.load()
      else this.reset()
    },
  },
  methods: {
    blankForm() {
      const f = {}
      EMPTY_JSON_FIELDS.forEach(k => { f[k] = [] })
      return f
    },
    reset() {
      this.tab = 'common'
      this.error = null
      this.saveError = null
      this.form = this.blankForm()
    },
    async load() {
      if (!this.personId) return
      this.loading = true; this.error = null
      try {
        const data = await getAdminUser(this.personId)
        // JSON-массивы могут прийти null/undefined — нормализуем в []
        EMPTY_JSON_FIELDS.forEach(k => {
          if (!Array.isArray(data[k])) data[k] = []
        })
        this.form = data
      } catch (e) {
        this.error = e?.payload?.detail || e?.message || this.$t('personnel.detailModal.loadError')
      } finally {
        this.loading = false
      }
    },
    addRow(field, template) {
      if (!Array.isArray(this.form[field])) this.form[field] = []
      this.form[field].push({ ...template })
    },
    delRow(field, i) {
      this.form[field].splice(i, 1)
    },
    async onSave() {
      if (!this.personId) return
      this.saving = true; this.saveError = null
      try {
        // Отправляем только редактируемые категории — без служебных полей.
        // ИИН read-only, id-шники (rank/position) отдельно править не даём.
        const payload = { ...this.form }
        delete payload.id
        delete payload.iin
        delete payload.arm_roles
        delete payload.position_code
        delete payload.rank_label
        delete payload.position_label
        delete payload.category_label
        await updateAdminUser(this.personId, payload)
        this.$emit('saved')
        this.$emit('close')
      } catch (e) {
        this.saveError = e?.payload?.detail
          || (e?.payload ? JSON.stringify(e.payload) : '')
          || e?.message
          || this.$t('personnel.detailModal.saveError')
      } finally {
        this.saving = false
      }
    },
    onClose() {
      if (this.saving) return
      this.$emit('close')
    },
  },
}
</script>

<style scoped>
.pdm__backdrop {
  position: fixed; inset: 0; background: rgba(15, 23, 42, .55);
  display: flex; align-items: center; justify-content: center; z-index: 9000;
}
.pdm {
  background: #fff; border-radius: 14px; width: min(1100px, 96vw);
  max-height: 92vh; display: flex; flex-direction: column;
  box-shadow: 0 24px 64px rgba(0,0,0,.25);
}
.pdm__head { display: flex; align-items: center; padding: 18px 22px; border-bottom: 1px solid #E5E7EB; }
.pdm__title { margin: 0; flex: 1 1 auto; font-size: 20px; font-weight: 700; color: #111827; }
.pdm__close { background: transparent; border: 0; font-size: 26px; line-height: 1; cursor: pointer; color: #6B7280; }

.pdm__tabs { display: flex; gap: 6px; padding: 12px 22px 0; border-bottom: 1px solid #E5E7EB; }
.pdm__tab {
  background: transparent; border: 0; padding: 10px 14px; cursor: pointer;
  font-size: 14px; font-weight: 600; color: #6B7280; border-bottom: 3px solid transparent;
  border-radius: 6px 6px 0 0;
}
.pdm__tab:hover { background: #F3F4F6; color: #111827; }
.pdm__tab.is-active { color: #2F78EE; border-bottom-color: #2F78EE; }

.pdm__body { padding: 20px 22px; overflow-y: auto; flex: 1 1 auto; }
.pdm__state { padding: 40px; text-align: center; color: #6B7280; font-size: 14px; }
.pdm__state--err { color: #B91C1C; }

.pdm__grid {
  display: grid; gap: 14px 16px;
  /* По ТЗ 14.09.2026: строго 2 колонки в ряду — раньше auto-fill растягивал
     до 3-4 колонок на широких экранах, дело выглядело перегруженным. */
  grid-template-columns: 1fr 1fr;
}
.pdm__field { display: flex; flex-direction: column; gap: 4px; font-size: 13px; color: #374151; }
.pdm__field > span { font-weight: 600; color: #6B7280; }
.pdm__field input, .pdm__field textarea {
  padding: 8px 10px; border: 1px solid #E5E7EB; border-radius: 8px;
  font-size: 14px; font-family: inherit; color: #111827; background: #fff;
  min-width: 0; box-sizing: border-box; width: 100%;
}
.pdm__field input:focus, .pdm__field textarea:focus {
  outline: 2px solid rgba(47,120,238,.25); border-color: #2F78EE;
}
.pdm__field textarea { resize: vertical; min-height: 60px; }
/* --wide теперь = «на всю ширину» (тоже 2 колонки при grid=1fr 1fr). */
.pdm__field--wide { grid-column: 1 / -1; }
.pdm__field--full { grid-column: 1 / -1; }
/*
  По ТЗ 03.09.2026 (вкладка «ТЫЛ»): чекбокс + подпись — в одну ровную строку
  слева. До фикса .pdm__field input { width:100% } растягивал сам checkbox,
  из-за чего пары «плыли лесенкой». Здесь принудительно фиксируем размер
  чекбокса, крепим содержимое к левому краю и обрезаем span от «--full»,
  чтобы подписи не убегали в правый край.
*/
.pdm__field--check {
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
}
.pdm__field--check > span { font-weight: 500; color: #111827; }
.pdm__field--check input[type="checkbox"] {
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  padding: 0;
  margin: 0;
  border: 1px solid #9CA3AF;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  accent-color: #2F78EE;
}
.pdm__field--check.pdm__field--full > span { flex: 0 1 auto; text-align: left; }

.pdm__jsontable { border: 1px solid #E5E7EB; border-radius: 10px; padding: 12px; background: #FAFAFB; }
.pdm__jsonhead { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.pdm__jsonhead strong { color: #111827; font-size: 14px; }
.pdm__addbtn {
  background: #EEF4FF; color: #2F78EE; border: 1px dashed #2F78EE;
  padding: 4px 10px; border-radius: 6px; font-size: 13px; cursor: pointer;
}
.pdm__jsonrow {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)) 32px;
  gap: 8px; margin-bottom: 8px;
}
.pdm__jsonrow input {
  padding: 6px 8px; border: 1px solid #E5E7EB; border-radius: 6px; font-size: 13px;
  min-width: 0; box-sizing: border-box;
}
.pdm__delbtn {
  background: #FEE2E2; color: #B91C1C; border: 0; border-radius: 6px;
  cursor: pointer; font-size: 16px;
}

.pdm__foot {
  display: flex; align-items: center; gap: 10px; justify-content: flex-end;
  padding: 14px 22px; border-top: 1px solid #E5E7EB;
}
.pdm__hint { flex: 1 1 auto; color: #B91C1C; font-size: 13px; }
.pdm__btn { padding: 9px 18px; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; border: 0; }
.pdm__btn--ghost { background: #F3F4F6; color: #111827; }
.pdm__btn--primary { background: #2F78EE; color: #fff; }
.pdm__btn--primary:disabled { opacity: .5; cursor: not-allowed; }
</style>
